import React, { useState, useEffect } from 'react';
import { Package, Sparkles, Heart, Clock, Users, ArrowRight, Check, Star, User, Mail, Phone, CreditCard, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { API } from '../config';
import axios from 'axios';

const FALLBACK_PACKAGES = [
  {
    id: 'relaxation-series', name: 'Relaxation Series', tagline: 'Best Value',
    description: '3 x 60-minute massages', originalPrice: 300, price: 269.99, savings: 30.01,
    duration: '3 sessions', features: ['Three 60-minute massage sessions', 'Mix and match any massage type', 'Complimentary Nagomi wellness drink per visit', 'Valid for 6 months', 'Shareable with family'],
    color: 'from-sage-500 to-sage-600', popular: false
  },
  {
    id: 'ultimate-relaxation', name: 'Ultimate Relaxation', tagline: 'Most Popular',
    description: '5 x 90-minute massages', originalPrice: 900, price: 499.99, savings: 400.01,
    duration: '5 sessions', features: ['Five 90-minute massage sessions', 'Mix and match any massage type', 'Complimentary Nagomi wellness drink per visit', 'Complimentary aromatherapy', 'Valid for 6 months', 'Shareable with family'],
    color: 'from-amber-500 to-amber-600', popular: true
  },
  {
    id: 'couples-retreat', name: 'Couples Retreat', tagline: 'Romantic',
    description: 'Side-by-side experience', originalPrice: 300, price: 249, savings: 51,
    duration: '90 min together', features: ['90-minute couples massage', 'Private couples room', 'Complimentary Nagomi wellness drinks for two', 'Rose petal setup', 'Relaxation lounge access'],
    color: 'from-rose-500 to-pink-500', popular: false
  },
  {
    id: 'wellness-journey', name: 'Wellness Journey', tagline: 'Premium',
    description: '5 x 60-minute massages', originalPrice: 500, price: 424.99, savings: 75.01,
    duration: '5 sessions', features: ['Five 60-minute massage sessions', 'Mix and match any massage type', 'Valid for 12 months', 'Priority booking', '10% off additional treatments', 'Shareable with family'],
    color: 'from-purple-500 to-indigo-500', popular: false
  }
];

const PackagesSection = () => {
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);
  const [purchaseStep, setPurchaseStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [buyerForm, setBuyerForm] = useState({ buyer_name: '', buyer_email: '', buyer_phone: '', nagomi_flavour: 'Yuzu Lemon' });

  useEffect(() => {
    fetch(`${API}/packages`)
      .then(res => res.json())
      .then(data => { if (data?.length) setPackages(data); })
      .catch(() => {});
  }, []);

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setPurchaseStep(1);
    setError('');
    setBuyerForm({ buyer_name: '', buyer_email: '', buyer_phone: '', nagomi_flavour: 'Yuzu Lemon' });
    setShowPackageModal(true);
  };

  const isFormValid = buyerForm.buyer_name && buyerForm.buyer_email && buyerForm.buyer_phone;

  const handlePurchase = async () => {
    if (!isFormValid || !selectedPackage) return;
    setLoading(true);
    setError('');

    const whatsappFallback = () => {
      const msg = encodeURIComponent(
        `Hi, I'd like to purchase the ${selectedPackage.name} package (€${selectedPackage.price}).\nName: ${buyerForm.buyer_name}\nEmail: ${buyerForm.buyer_email}\nPhone: ${buyerForm.buyer_phone}\nNagomi flavour: ${buyerForm.nagomi_flavour}`
      );
      window.open(`https://wa.me/353857747474?text=${msg}`, '_blank');
    };

    try {
      const res = await axios.post(`${API}/packages/purchase`, {
        ...buyerForm,
        package_id: selectedPackage.id,
        package_name: selectedPackage.name,
        package_price: selectedPackage.price,
      });
      const purchaseId = res.data.purchase_id;

      try {
        const checkoutRes = await axios.post(
          `${API}/packages/purchase/${purchaseId}/checkout`,
          null,
          { params: { return_url: window.location.origin + '/package-confirmed' } }
        );
        window.location.href = checkoutRes.data.checkout_url;
      } catch (checkoutErr) {
        setError('Payment setup failed. You can complete your purchase via WhatsApp.');
        setLoading(false);
      }
    } catch (err) {
      setError('Unable to process your request. You can complete your purchase via WhatsApp.');
      setLoading(false);
    }
  };

  return (
    <section id="packages" className="py-28 bg-bone relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-sage-100/30 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-luxury-gold text-xs uppercase tracking-[0.3em] font-medium">Save More</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-luxury-green mt-4 mb-6">
            Treatment
            <span className="block italic text-luxury-green/70 mt-1">Packages</span>
          </h2>
          <p className="text-stone-500 font-light">
            Get more value with our specially curated massage packages. Buy directly online.
          </p>
          <a
            href="https://www.drinknagomi.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mt-6 opacity-80 hover:opacity-100 transition-opacity"
            data-testid="nagomi-partnership-link"
          >
            <span className="text-stone-400 text-xs uppercase tracking-[0.2em]">In partnership with</span>
            <img src="/nagomi-logo.png" alt="Nagomi" className="h-10 w-10 object-contain" />
            <span className="text-luxury-green font-semibold text-sm tracking-wide">NAGOMI</span>
          </a>
          <p className="text-stone-400 text-xs mt-2 font-light">
            Every package includes a complimentary Nagomi wellness drink &mdash; balanced, botanical, gut-to-mind energy.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative bg-white rounded-2xl p-6 shadow-lg border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                pkg.popular ? 'border-luxury-gold' : 'border-stone-100'
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="bg-luxury-green text-luxury-gold border border-luxury-gold/40 px-4 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <Star size={12} className="fill-luxury-gold text-luxury-gold" />
                    {pkg.tagline}
                  </div>
                </div>
              )}
              {!pkg.popular && (
                <div className="mb-4">
                  <span className="inline-block text-luxury-green text-xs font-semibold uppercase tracking-wider">
                    {pkg.tagline}
                  </span>
                </div>
              )}
              <h3 className="text-xl font-semibold text-stone-800 mb-1 mt-2">{pkg.name}</h3>
              <p className="text-stone-500 text-sm mb-4">{pkg.description}</p>
              <div className="mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-luxury-green">
                    €{pkg.price}
                  </span>
                  <span className="text-stone-400 line-through text-sm">€{pkg.originalPrice}</span>
                </div>
                <div className="inline-flex items-center gap-1 bg-luxury-green/10 text-luxury-green px-2 py-0.5 rounded-full text-xs font-medium mt-1">
                  <Sparkles size={12} />
                  Save €{pkg.savings}
                </div>
              </div>
              <div className="flex items-center gap-2 text-stone-500 text-sm mb-4">
                <Clock size={14} />
                <span>{pkg.duration}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {pkg.features.slice(0, 3).map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-stone-600 text-sm">
                    <Check size={14} className={`mt-0.5 ${pkg.popular ? 'text-luxury-gold' : 'text-luxury-green'}`} />
                    <span>{feature}</span>
                  </li>
                ))}
                {pkg.features.length > 3 && (
                  <li className="text-stone-400 text-sm">+{pkg.features.length - 3} more benefits</li>
                )}
              </ul>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleSelectPackage(pkg)}
                  className={`flex-1 py-5 rounded-full font-medium transition-all ${
                    pkg.popular
                      ? 'bg-luxury-green hover:bg-luxury-green-600 text-luxury-gold'
                      : 'bg-luxury-green-800 hover:bg-luxury-green text-white'
                  }`}
                  data-testid={`select-${pkg.id}-btn`}
                >
                  Buy Package
                  <CreditCard size={16} className="ml-2" />
                </Button>
                <Button
                  onClick={() => {
                    const msg = `Amazing massage deal at Dublin Holistic Massage!\n\n${pkg.name} - ${pkg.description}\n€${pkg.price} (save €${pkg.savings}!)\n\nBook here: https://www.dublinholisticmassages.com`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                  className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full w-12 py-5 p-0 flex items-center justify-center flex-shrink-0"
                  data-testid={`share-${pkg.id}-btn`}
                >
                  <MessageCircle size={18} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 bg-luxury-green/10 px-6 py-3 rounded-full border border-luxury-green/20">
            <Heart className="text-luxury-green" size={18} />
            <span className="text-luxury-green">All packages make perfect gifts!</span>
          </div>
        </div>
      </div>

      {/* Package Purchase Modal */}
      <Dialog open={showPackageModal} onOpenChange={setShowPackageModal}>
        <DialogContent className="max-w-md bg-white rounded-3xl p-6 sm:p-8" data-testid="package-purchase-modal">
          <div>
            <div className="text-center mb-6">
              <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${selectedPackage?.color} rounded-full mb-4`}>
                <Package className="text-white" size={24} />
              </div>
              <DialogTitle className="text-2xl font-semibold text-stone-800 mb-1">
                {selectedPackage?.name}
              </DialogTitle>
              <DialogDescription className="text-stone-500">
                {selectedPackage?.description}
              </DialogDescription>
            </div>

            {purchaseStep === 1 && (
              <div className="space-y-4">
                <div className="bg-stone-50 rounded-xl p-4 mb-4">
                  <p className="font-medium text-stone-800 mb-3 text-sm">Package includes:</p>
                  <ul className="space-y-1.5">
                    {selectedPackage?.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-stone-600 text-sm">
                        <Check size={14} className="mt-0.5 text-sage-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-baseline justify-between border-t pt-4">
                  <div>
                    <span className="text-stone-400 line-through text-sm">€{selectedPackage?.originalPrice}</span>
                    <div className="inline-flex items-center gap-1 bg-green-50 text-green-600 px-2 py-0.5 rounded-full text-xs font-medium ml-2">
                      Save €{selectedPackage?.savings}
                    </div>
                  </div>
                  <span className={`text-3xl font-bold bg-gradient-to-r ${selectedPackage?.color} bg-clip-text text-transparent`}>
                    €{selectedPackage?.price}
                  </span>
                </div>

                <Button
                  onClick={() => setPurchaseStep(2)}
                  className={`w-full bg-gradient-to-r ${selectedPackage?.color} hover:opacity-90 text-white py-5 rounded-full font-medium`}
                  data-testid="package-proceed-btn"
                >
                  Buy Now
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            )}

            {purchaseStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-stone-800">Your Details</h3>
                <div className="space-y-3">
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                      placeholder="Full name"
                      value={buyerForm.buyer_name}
                      onChange={(e) => setBuyerForm({ ...buyerForm, buyer_name: e.target.value })}
                      className="pl-9 text-sm h-10"
                      data-testid="pkg-buyer-name"
                    />
                  </div>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={buyerForm.buyer_email}
                      onChange={(e) => setBuyerForm({ ...buyerForm, buyer_email: e.target.value })}
                      className="pl-9 text-sm h-10"
                      data-testid="pkg-buyer-email"
                    />
                  </div>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                      type="tel"
                      placeholder="+353 85 XXX XXXX"
                      value={buyerForm.buyer_phone}
                      onChange={(e) => setBuyerForm({ ...buyerForm, buyer_phone: e.target.value })}
                      className="pl-9 text-sm h-10"
                      data-testid="pkg-buyer-phone"
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-stone-600 mb-2 flex items-center gap-2">
                    <img src="/nagomi-logo.png" alt="Nagomi" className="w-5 h-5 object-contain" />
                    Pick your complimentary Nagomi flavour
                  </p>
                  <div className="grid grid-cols-2 gap-2" data-testid="nagomi-flavour-picker">
                    {['Yuzu Lemon', 'Orange Blossom & Peach'].map((flavour) => (
                      <button
                        key={flavour}
                        type="button"
                        onClick={() => setBuyerForm({ ...buyerForm, nagomi_flavour: flavour })}
                        className={`rounded-xl border px-3 py-2.5 text-xs font-medium transition-all ${
                          buyerForm.nagomi_flavour === flavour
                            ? 'border-luxury-green bg-luxury-green/5 text-luxury-green'
                            : 'border-stone-200 text-stone-500 hover:border-stone-300'
                        }`}
                        data-testid={`nagomi-flavour-${flavour === 'Yuzu Lemon' ? 'yuzu' : 'peach'}`}
                      >
                        {flavour}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-stone-50 rounded-xl p-4 text-sm">
                  <div className="flex justify-between mb-1">
                    <span className="text-stone-500">Package</span>
                    <span className="font-medium">{selectedPackage?.name}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-stone-500">Nagomi drink</span>
                    <span className="font-medium text-luxury-green" data-testid="nagomi-flavour-summary">{buyerForm.nagomi_flavour}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-base pt-2 border-t mt-2">
                    <span>Total</span>
                    <span className={`bg-gradient-to-r ${selectedPackage?.color} bg-clip-text text-transparent`}>
                      €{selectedPackage?.price}
                    </span>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center" data-testid="package-error">
                    <p className="text-red-700 text-sm mb-3">{error}</p>
                    <Button
                      onClick={() => {
                        const msg = encodeURIComponent(
                          `Hi, I'd like to purchase the ${selectedPackage?.name} package (€${selectedPackage?.price}).\nName: ${buyerForm.buyer_name}\nEmail: ${buyerForm.buyer_email}\nPhone: ${buyerForm.buyer_phone}\nNagomi flavour: ${buyerForm.nagomi_flavour}`
                        );
                        window.open(`https://wa.me/353857747474?text=${msg}`, '_blank');
                      }}
                      className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full px-6 py-2 text-sm"
                      data-testid="package-whatsapp-fallback"
                    >
                      Complete via WhatsApp
                    </Button>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setPurchaseStep(1); setError(''); }}
                    className="flex-1 rounded-full border-stone-300"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handlePurchase}
                    disabled={!isFormValid || loading}
                    className={`flex-1 bg-gradient-to-r ${selectedPackage?.color} hover:opacity-90 text-white rounded-full disabled:opacity-50`}
                    data-testid="package-pay-btn"
                  >
                    {loading ? 'Processing...' : (
                      <>
                        <CreditCard size={16} className="mr-2" />
                        Pay €{selectedPackage?.price}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PackagesSection;
